import { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import { FC, useRef } from "react";
import { Upload, Loader2 } from "lucide-react";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";

interface Props {
	stepNumber: number;
	placeholder: string;
	control: any;
	error: boolean;
	upload: (file: File, uploadType: string) => void;
	uuid: string | null;
	done: boolean;
	uploading: boolean;
	setValue: UseFormSetValue<BannerFormState>;
}

const VideoUpload: FC<Props> = ({
	stepNumber,
	placeholder,
	control,
	upload,
	uuid,
	uploading,
	setValue
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	const bannerType = useWatch({ name: "bannerType", control });

	const value = useWatch({ name: `steps.${stepNumber - 1}.value`, control });

	const isVideoRequired =
		bannerType === "videoBanner" || bannerType === "promotionVideo";

	const {
		field: { onChange: uuidOnChange, value: uuidValue, ...inputProps }
	} = useController({
		name: `steps.${stepNumber - 1}.value`,
		control,
		rules: { required: isVideoRequired }
	});

	const {
		field: { onChange: fileNameOnChange }
	} = useController({
		name: `steps.${stepNumber - 1}.file`,
		control
	});

	const videoUuid = (uuid || value) as string | undefined;
	const {
		url: videoUrl,
		thumbnail,
		processingStatus
	} = useAzureVideoDownload("video", "", videoUuid || null);

	const processFile = (file: File) => {
		const reader = new FileReader();

		reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
			if (!progressEvent?.target) return;

			setValue(
				`steps.${stepNumber - 1}.preview`,
				progressEvent.target.result as string
			);

			upload(file, "video");
		};

		reader.readAsDataURL(file);
		fileNameOnChange(file.name);
	};

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		if (!target?.files) return;
		processFile(target.files[0]);
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (!file) return;

		const isValid =
			file.type === "video/mp4" || file.type === "video/quicktime";
		if (!isValid) {
			alert("Only MP4 / MOV file types can be selected");
			return;
		}

		processFile(file);
	};

	useEffect(() => {
		if (!uuid) return;

		setValue(`steps.${stepNumber - 1}.value`, uuid);
		uuidOnChange(uuid);
	}, [uuid]);

	const hasVideo = !!(uuid || value);
	const isProcessing = processingStatus === "Processing";

	return (
		<div className="max-w-[480px]">
			<input
				type="file"
				onChange={onFileSelected}
				accept="video/mp4,video/quicktime"
				{...inputProps}
				name={`steps.${stepNumber - 1}`}
				ref={inputRef}
				style={{ display: "none" }}
				aria-label="file-upload"
			/>

			{hasVideo && !uploading ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
						{videoUrl ? (
							<video
								src={videoUrl}
								poster={thumbnail || undefined}
								controls
								className="w-full object-cover"
								style={{ aspectRatio: "16/9" }}
								preload="metadata"
							/>
						) : isProcessing ? (
							<div
								className="flex flex-col items-center justify-center gap-2 bg-foreground"
								style={{ aspectRatio: "16/9" }}
							>
								<Loader2 className="h-8 w-8 text-white animate-spin" />
								<span className="text-sm text-white/70">
									Processing video...
								</span>
							</div>
						) : thumbnail ? (
							<div
								className="w-full bg-no-repeat bg-cover bg-center"
								style={{
									backgroundImage: `url(${thumbnail})`,
									aspectRatio: "16/9"
								}}
							/>
						) : (
							<div
								className="flex items-center justify-center bg-foreground"
								style={{ aspectRatio: "16/9" }}
							>
								<span className="text-sm text-white/50">Video uploaded</span>
							</div>
						)}
					</div>
					<button
						type="button"
						className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
						onClick={() => inputRef?.current?.click()}
					>
						Change video
					</button>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => !uploading && inputRef?.current?.click()}
					onKeyDown={(e) => {
						if ((e.key === "Enter" || e.key === " ") && !uploading) {
							e.preventDefault();
							inputRef?.current?.click();
						}
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary-lighter"
							: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50",
						uploading ? "pointer-events-none opacity-60" : ""
					].join(" ")}
				>
					{uploading ? (
						<>
							<Loader2 className="h-6 w-6 text-primary animate-spin" />
							<span className="text-sm text-muted-foreground">
								Uploading...
							</span>
						</>
					) : (
						<>
							<Upload className="h-6 w-6 text-muted-foreground" />
							<div>
								<p className="text-sm font-medium text-foreground">
									Drag video here or click to browse
								</p>
								<p className="text-xs text-muted-foreground mt-1">MP4 or MOV</p>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default VideoUpload;
